import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { isCreatorRole } from '../types/roles';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

type Props = { children: ReactNode };

/**
 * Studio guard: requires Supabase session AND `public.users.role` of creator/admin/superadmin.
 *
 * Also gates on the `creator_program_enabled` feature flag — if the flag is off,
 * non-staff creators are redirected to /library. Staff (admin/superadmin) bypass
 * the flag so they can still operate Studio for testing/grants.
 */
export function RequireCreator({ children }: Props) {
  const session = useAppStore((s) => s.session);
  const userRole = useAppStore((s) => s.userRole);
  const location = useLocation();
  const { enabled: programEnabled, loading: flagLoading } = useFeatureFlag('creator_program_enabled');

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (!isCreatorRole(userRole)) {
    return <Navigate to="/library" replace />;
  }

  const isStaff = userRole === 'admin' || userRole === 'superadmin';

  if (flagLoading && !isStaff) {
    return (
      <div className="min-h-dvh w-full flex items-center justify-center text-th-secondary text-sm">
        Loading…
      </div>
    );
  }

  if (!programEnabled && !isStaff) {
    return (
      <div className="min-h-dvh w-full flex flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-xl font-semibold text-th-primary">Creator Studio is rolling out</h1>
        <p className="text-th-secondary max-w-md">
          We&apos;re inviting creators in waves. You have the role, but the program isn&apos;t open yet — you&apos;ll see Studio
          unlock here as soon as we flip the switch.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
